'use client';

import { __assign, __awaiter, __generator } from '../_virtual/_tslib.js';
import React__default, { useState, useRef, useCallback, useEffect } from 'react';
import Popup from '../Popup/index.js';
import ApplyJobsProvider, { useApplyJobsProps } from './store/provider.js';
export { default as ApplyJobsProvider } from './store/provider.js';
import { createTranslator } from '../Utils/helpers.js';
import { ModalContainer, LeftLayout, RightLayout } from './styles.js';
import translations from './translations.js';
import deepEqual from '../Form/utils/deepEqual.js';
import PopupWarning from '../PopupWarning/index.js';
import { getCookie } from '../Utils/cookie.js';
import useHog from '../PostHog/hooks/useHog/index.js';
import { getJobLevelList } from '../services/resumes/index.js';
import '../UploadResumeModal/type.js';
import { ELanguage } from '../Utils/constants.js';
import ModelError from '../services/model/index.js';
import useMediaQuery from '../hooks/useMediaQuery.js';
import 'react-dom';
import '../hooks/useMakeSearchable/types.js';
import { EApplyType } from '../services/resumes/types.js';
import { applyJobs } from '../services/jobs/index.js';
import { applySuccessLink, getJobUrlParams } from '../services/jobs/helper.js';
import { trackingABJobAnalysis } from '../services/tracking/applyJob.js';
import { INTERN_LEVEL, FRESHER_LEVEL, MANAGER_LEVEL, DIRECTOR_LEVEL, MODAL_SCROLLABLE_BODY_MODAL, COLLAPSE_ANIMATION_DURATION } from './constants.js';
import { updateUserInfo } from './services/update-info.js';

var ApplyJobForm = React__default.lazy(function () { return import('./components/ApplyJobForm/index.js'); });
var JobInfoCard = React__default.lazy(function () { return import('./components/JobInfoCard/index.js'); });
var ModalFooter = React__default.lazy(function () { return import('./components/Footer/index.js'); });
function ApplyJobsMain(_a) {
    var _this = this;
    var _b;
    var isOpen = _a.isOpen, onClose = _a.onClose;
    /*
     * Store
     * */
    var props = useApplyJobsProps();
    var tracking = useHog().tracking;
    /*
     * State
     * */
    var _c = useState(null), jobLevels = _c[0], setJobLevels = _c[1];
    var _d = useState(false), isLoading = _d[0], setLoading = _d[1];
    var _e = useState(false), showPopupApply = _e[0], setShowPopupApply = _e[1];
    var _f = useState(false), showPopupWarning = _f[0], setShowPopupWarning = _f[1];
    var _g = useState(''), errorSystemMessage = _g[0], setErrorSystemMessage = _g[1];
    var _h = useState(0), footerHeight = _h[0], setFooterHeight = _h[1];
    /*
     * Refs and values
     * */
    var scrollSectionRef = useRef(null);
    var scrollContainerRef = useRef(null);
    var formRef = useRef(null);
    var mobileBreakpoint = useMediaQuery('(max-width: 768px)');
    var errorSystemRef = useRef(null);
    var modalFooterRef = useRef(null);
    /*
     * Get job levels and open popup
     * */
    var handlePopupAction = useCallback(function () {
        var _a, _b, _c, _d;
        var isUserLowLevel = ((_a = props.user) === null || _a === void 0 ? void 0 : _a.jobLevelId) === INTERN_LEVEL || ((_b = props.user) === null || _b === void 0 ? void 0 : _b.jobLevelId) === FRESHER_LEVEL;
        var isJobHighLevel = ((_c = props.jobInfo) === null || _c === void 0 ? void 0 : _c.jobLevelId) === MANAGER_LEVEL || ((_d = props.jobInfo) === null || _d === void 0 ? void 0 : _d.jobLevelId) === DIRECTOR_LEVEL;
        var isAllowApplyJobs = !(isJobHighLevel && isUserLowLevel);
        if (isAllowApplyJobs) {
            setShowPopupApply(isOpen);
        }
        else {
            setShowPopupWarning(isOpen);
        }
    }, [props.user, props.jobInfo, isOpen]);
    useEffect(function () {
        if (isOpen) {
            if (!jobLevels || jobLevels.length < 1) {
                (function () { return __awaiter(_this, void 0, void 0, function () {
                    var jobLevels;
                    var _a;
                    return __generator(this, function (_b) {
                        switch (_b.label) {
                            case 0: return [4 /*yield*/, getJobLevelList(props.langCode, (_a = props.baseMsUrl) !== null && _a !== void 0 ? _a : '')];
                            case 1:
                                jobLevels = _b.sent();
                                if (jobLevels && jobLevels.length > 0) {
                                    setJobLevels(jobLevels);
                                }
                                return [2 /*return*/];
                        }
                    });
                }); })();
            }
            handlePopupAction();
        }
    }, [props.langCode, props.baseMsUrl, isOpen, jobLevels, handlePopupAction]);
    /*
     * Get footer height
     * */
    useEffect(function () {
        var getFooterHeight = function () {
            var _a;
            setFooterHeight(((_a = modalFooterRef.current) === null || _a === void 0 ? void 0 : _a.offsetHeight) || 0);
        };
        if (modalFooterRef.current) {
            getFooterHeight();
            var observer_1 = new MutationObserver(getFooterHeight);
            observer_1.observe(modalFooterRef.current, {
                childList: true,
                subtree: true
            });
            return function () {
                observer_1.disconnect();
            };
        }
        return function () { };
    }, [modalFooterRef.current]);
    /**
     * Get scroll container
     * */
    useEffect(function () {
        var scrollableBodyModal = document.querySelector(".".concat(MODAL_SCROLLABLE_BODY_MODAL));
        scrollContainerRef.current = mobileBreakpoint ? scrollableBodyModal : scrollSectionRef.current;
    });
    /*
     * Scroll to system error if has
     * */
    useEffect(function () {
        if (errorSystemMessage && errorSystemRef.current) {
            errorSystemRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [errorSystemMessage]);
    /*
     * Scroll to warning status
     * */
    useEffect(function () {
        if (errorSystemMessage) {
            if (scrollContainerRef.current) {
                var scrollContainer_1 = scrollContainerRef.current;
                /*
                 * Get scrolling container
                 * */
                var scrollIntoError = function () {
                    scrollContainer_1.scrollTo({ top: scrollContainer_1.scrollHeight, behavior: 'smooth' });
                };
                var timeOut_1 = setTimeout(scrollIntoError, COLLAPSE_ANIMATION_DURATION);
                return function () {
                    clearTimeout(timeOut_1);
                };
            }
        }
        return function () { };
    }, [errorSystemMessage]);
    var handleUserLevelName = useCallback(function () {
        var level = jobLevels === null || jobLevels === void 0 ? void 0 : jobLevels.find(function (level) { var _a; return level.id === ((_a = props.user) === null || _a === void 0 ? void 0 : _a.jobLevelId); });
        return props.langCode === ELanguage.EN ? level === null || level === void 0 ? void 0 : level.attributes.nameEn : level === null || level === void 0 ? void 0 : level.attributes.nameVi;
    }, [props.langCode, jobLevels, props.user]);
    var handleSubmitForm = function (values) { return __awaiter(_this, void 0, void 0, function () {
        var birthday, updateUserInfoBody, updateUserInfoResponse, initialValues, isUpdatedBasicInfo, isUpdatedWorkingPreference, updatedBlock, resumeType, body_1, jobSearchParams, apply, info, postHogProperties, ABAnalysis, link, error_1;
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w;
        return __generator(this, function (_x) {
            switch (_x.label) {
                case 0:
                    if (!props.jobInfo) {
                        /*
                         * Return immediately if don't have job info
                         * */
                        return [2 /*return*/];
                    }
                    if (values.initialSubmitValues.applyResume.resumeId === -1) {
                        /*
                         * Scroll to error if resumeId equal -1
                         * */
                        if (scrollContainerRef.current) {
                            scrollContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
                        }
                        return [2 /*return*/];
                    }
                    setLoading(true);
                    _x.label = 1;
                case 1:
                    _x.trys.push([1, 8, , 9]);
                    birthday = new Date(values.birthday).toISOString();
                    updateUserInfoBody = {
                        resumeId: 0,
                        currentJobFunction: values.currentJobFunction,
                        currentIndustries: values.currentIndustries,
                        currentSalary: values.currentSalary,
                        firstName: values.firstName,
                        lastName: values.lastName,
                        jobTitle: values.jobTitle,
                        jobLevelId: values.jobLevelId,
                        highestDegreeId: values.highestDegreeId,
                        yearsExperience: values.yearsExperience,
                        email: values.email,
                        phone: values.phone,
                        birthday: birthday,
                        genderId: values.genderId,
                        countryId: values.countryId,
                        cityId: values.cityId,
                        address: values.address,
                        nationalityId: values.nationalityId,
                        maritalStatusId: values.maritalStatusId,
                        districtId: values.districtId,
                        locations: values.locations,
                        companyIndustries: values.companyIndustries,
                        desiredJobLevelId: values.desiredJobLevelId,
                        salary: values.salary,
                        benefits: values.benefits,
                        jobFunction: values.jobFunction
                    };
                    return [4 /*yield*/, updateUserInfo({ accessToken: props.accessToken, baseMsUrl: props.baseMsUrl, langCode: props.langCode }, updateUserInfoBody)
                        /*
                         * Handle error of updating information
                         * */
                    ];
                case 2:
                    updateUserInfoResponse = _x.sent();
                    /*
                     * Handle error of updating information
                     * */
                    if (updateUserInfoResponse instanceof ModelError) {
                        setLoading(false);
                        setErrorSystemMessage((_b = (_a = updateUserInfoResponse.error) === null || _a === void 0 ? void 0 : _a.message) !== null && _b !== void 0 ? _b : '');
                        return [2 /*return*/];
                    }
                    initialValues = values.initialSubmitValues.initialValues;
                    isUpdatedBasicInfo = !deepEqual({
                        firstName: updateUserInfoBody.firstName,
                        lastName: updateUserInfoBody.lastName,
                        yearsExperience: updateUserInfoBody.yearsExperience,
                        highestDegreeId: updateUserInfoBody.highestDegreeId,
                        currentJobFunction: updateUserInfoBody.currentJobFunction,
                        currentIndustries: updateUserInfoBody.currentIndustries,
                        currentSalary: updateUserInfoBody.currentSalary,
                        jobLevelId: updateUserInfoBody.jobLevelId,
                        phone: updateUserInfoBody.phone,
                        nationalityId: updateUserInfoBody.nationalityId,
                        genderId: updateUserInfoBody.genderId,
                        maritalStatusId: updateUserInfoBody.maritalStatusId,
                        countryId: updateUserInfoBody.countryId
                    }, {
                        firstName: initialValues.firstName,
                        lastName: initialValues.lastName,
                        yearsExperience: initialValues.yearsExperience,
                        highestDegreeId: initialValues.highestDegreeId,
                        currentJobFunction: initialValues.currentJobFunction,
                        currentIndustries: initialValues.currentIndustries,
                        currentSalary: initialValues.currentSalary,
                        jobLevelId: initialValues.jobLevelId,
                        phone: initialValues.phone,
                        nationalityId: initialValues.nationalityId,
                        genderId: initialValues.genderId,
                        maritalStatusId: initialValues.maritalStatusId,
                        countryId: initialValues.countryId
                    });
                    isUpdatedWorkingPreference = !deepEqual({
                        locations: updateUserInfoBody.locations,
                        benefits: updateUserInfoBody.benefits,
                        salary: updateUserInfoBody.salary,
                        desiredJobLevelId: updateUserInfoBody.desiredJobLevelId,
                        jobFunction: updateUserInfoBody.jobFunction,
                        companyIndustries: updateUserInfoBody.companyIndustries
                    }, {
                        locations: initialValues.locations,
                        benefits: initialValues.benefits,
                        salary: initialValues.salary,
                        desiredJobLevelId: initialValues.desiredJobLevelId,
                        jobFunction: initialValues.jobFunction,
                        companyIndustries: initialValues.companyIndustries
                    });
                    updatedBlock = [];
                    if (isUpdatedBasicInfo) {
                        updatedBlock.push('basicInfo');
                    }
                    if (isUpdatedWorkingPreference) {
                        updatedBlock.push('workingPreferences');
                    }
                    if (updatedBlock.length > 0) {
                        tracking({
                            eventName: 'js_profile_management:update_myprofile',
                            properties: {
                                fromPage: 'apply_form',
                                blockUpdated: updatedBlock.join(', ')
                            }
                        });
                    }
                    resumeType = ((_c = values.initialSubmitValues.applyResume.type) === null || _c === void 0 ? void 0 : _c.includes(EApplyType.lastAttachment))
                        ? EApplyType.lastAttachment
                        : values.initialSubmitValues.applyResume.type;
                    body_1 = __assign(__assign({ 'jobIds[]': (_d = props.jobInfo) === null || _d === void 0 ? void 0 : _d.jobId, resumeId: (_f = (_e = values.initialSubmitValues.applyResume) === null || _e === void 0 ? void 0 : _e.resumeId) !== null && _f !== void 0 ? _f : 0, appliedType: resumeType }, (!values.coverLetter ? undefined : { coverLetter: values.coverLetter })), (((_g = props.user) === null || _g === void 0 ? void 0 : _g.userIp) ? { client_ip: (_h = props.user) === null || _h === void 0 ? void 0 : _h.userIp } : undefined));
                    jobSearchParams = getJobUrlParams((_k = (_j = props.jobInfo) === null || _j === void 0 ? void 0 : _j.jobUrl) !== null && _k !== void 0 ? _k : '');
                    return [4 /*yield*/, applyJobs((_l = props.baseMsUrl) !== null && _l !== void 0 ? _l : '', props.accessToken, body_1, props.langCode, jobSearchParams)
                        /*
                         * Handle error of apply job
                         * */
                    ];
                case 3:
                    apply = _x.sent();
                    /*
                     * Handle error of apply job
                     * */
                    if (apply instanceof ModelError) {
                        setLoading(false);
                        setErrorSystemMessage((_o = (_m = apply.error) === null || _m === void 0 ? void 0 : _m.message) !== null && _o !== void 0 ? _o : '');
                        return [2 /*return*/];
                    }
                    if (!(apply && (apply === null || apply === void 0 ? void 0 : apply["".concat(props.jobInfo.jobId)]))) return [3 /*break*/, 7];
                    info = apply === null || apply === void 0 ? void 0 : apply["".concat(props.jobInfo.jobId)];
                    if (!(info.code === 200)) return [3 /*break*/, 6];
                    // PostHog tracking
                    if (props.hogTracking) {
                        postHogProperties = __assign(__assign({}, props.hogTracking.properties), { resumeId: body_1.resumeId, resumeType: body_1.appliedType === EApplyType.online ? 'online' : 'attachment', resumeLastedUpload: (_q = (_p = values.initialSubmitValues.resumeInfo) === null || _p === void 0 ? void 0 : _p.attributes.find(function (item) { return item.id === body_1.resumeId; })) === null || _q === void 0 ? void 0 : _q.lastProcessedOn, invitation: false });
                        // Tracking job invitation
                        if (props.jobInfo.jobInvitation && props.jobInfo.jobInvitation.jobInvitationId > 0) {
                            postHogProperties.invitationId = props.jobInfo.jobInvitation.jobInvitationId;
                        }
                        tracking({
                            eventName: 'js_job_management:job_apply_single_success' ,
                            properties: postHogProperties
                        }, {
                            transport: 'sendBeacon'
                        });
                    }
                    else {
                        tracking({
                            eventName: 'js_job_management:job_apply_preview_success',
                            properties: {
                                resumeId: body_1.resumeId,
                                resumeType: body_1.appliedType === EApplyType.online ? 'online' : 'attachment',
                                jobId: (_r = props.jobInfo) === null || _r === void 0 ? void 0 : _r.jobId
                            }
                        }, {
                            transport: 'sendBeacon'
                        });
                    }
                    ABAnalysis = getCookie('ABJobAnalysis');
                    if (!(props.enableTracking && ABAnalysis)) return [3 /*break*/, 5];
                    // Wait for tracking first then redirect later
                    return [4 /*yield*/, trackingABJobAnalysis((_s = props.baseMsUrl) !== null && _s !== void 0 ? _s : '', {
                            userId: (_u = (_t = props.user) === null || _t === void 0 ? void 0 : _t.userId) !== null && _u !== void 0 ? _u : -1,
                            version: ABAnalysis,
                            action: 'apply'
                        })];
                case 4:
                    // Wait for tracking first then redirect later
                    _x.sent();
                    _x.label = 5;
                case 5:
                    link = applySuccessLink((_v = props.baseUrl) !== null && _v !== void 0 ? _v : '', props.translator, props.jobInfo);
                    setLoading(false);
                    setShowPopupApply(false);
                    onClose && onClose();
                    link && window.location.assign(link);
                    return [3 /*break*/, 7];
                case 6:
                    setErrorSystemMessage((_w = info.message) !== null && _w !== void 0 ? _w : '');
                    setLoading(false);
                    _x.label = 7;
                case 7:
                    setLoading(false);
                    return [3 /*break*/, 9];
                case 8:
                    error_1 = _x.sent();
                    console.error(error_1);
                    setLoading(false);
                    return [3 /*break*/, 9];
                case 9: return [2 /*return*/];
            }
        });
    }); };
    var handleSubmitError = useCallback(function (errorFieldId) {
        if (scrollContainerRef.current) {
            /*
             * Get scrolling container
             * */
            var scrollContainer = scrollContainerRef.current;
            var firstErrorElement = document.querySelector("#".concat(errorFieldId));
            scrollContainer.scrollTo({
                behavior: 'smooth',
                top: (firstErrorElement === null || firstErrorElement === void 0 ? void 0 : firstErrorElement.offsetTop) || 0
            });
        }
    }, []);
    return (React__default.createElement(React__default.Fragment, null,
        showPopupWarning ? (React__default.createElement(PopupWarning, { isShow: showPopupWarning, langCode: props.langCode, onClose: function () {
                setShowPopupWarning(false);
                onClose && onClose();
            }, onAccept: function () {
                setShowPopupApply(true);
                setShowPopupWarning(false);
            }, title: props.translator('Application confirmation'), warningTxt: props.translator('Do you want to continue to apply?') },
            props.translator('Your current job level'),
            ": ", (_b = handleUserLevelName()) !== null && _b !== void 0 ? _b : '',
            "\u00A0",
            props.translator('is not matching to this job requirement'),
            ".")) : null,
        React__default.createElement(Popup, { onClose: function () {
                setShowPopupApply(false);
                setShowPopupWarning(false);
                setErrorSystemMessage('');
                onClose && onClose();
            }, onClickOutside: function () {
                setShowPopupApply(false);
                setShowPopupWarning(false);
                setErrorSystemMessage('');
                onClose && onClose();
            }, text: {
                header: props.translator('Apply job')
            }, isShow: showPopupApply, lang: props.langCode, styleOption: { size: 'lg' }, custom: {
                additionalFooterHeight: footerHeight,
                footer: (React__default.createElement(React__default.Suspense, { fallback: React__default.createElement(React__default.Fragment, null) },
                    React__default.createElement(ModalFooter, { ref: modalFooterRef, isLoading: isLoading, onApply: function () { var _a; return (_a = formRef.current) === null || _a === void 0 ? void 0 : _a.submit(); } }))),
                bodyClass: MODAL_SCROLLABLE_BODY_MODAL
            } },
            React__default.createElement(ModalContainer, { footerHeight: footerHeight },
                React__default.createElement(LeftLayout, { xs: 24, sm: 8 },
                    React__default.createElement(React__default.Suspense, { fallback: React__default.createElement(React__default.Fragment, null) },
                        React__default.createElement(JobInfoCard, null))),
                React__default.createElement(RightLayout, { xs: 24, sm: 'fill', ref: scrollSectionRef },
                    React__default.createElement(React__default.Suspense, { fallback: React__default.createElement(React__default.Fragment, null) },
                        React__default.createElement(ApplyJobForm, { ref: formRef, handleSubmit: handleSubmitForm, handleSumbitError: handleSubmitError, errorSystemMessage: errorSystemMessage, onCloseErrorSystemMessage: setErrorSystemMessage, requestScrollToFirstErrorElement: true })))))));
}
function ApplyJobs(props) {
    var isMobile = useMediaQuery('(max-width: 768px)') || false;
    return (React__default.createElement(ApplyJobsProvider, { globalProps: __assign(__assign({}, props), { translator: createTranslator({ translations: translations, currentLanguage: props.langCode }), isMobile: isMobile }) },
        React__default.createElement(ApplyJobsMain, { isOpen: props.isOpen, onClose: props.onClose })));
}

export { ApplyJobForm, ApplyJobs as default };
//# sourceMappingURL=index.js.map
